import * as THREE from 'three'
import type { MetaVariant, VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb } from '../shaderChunks'

const VARIANT_ID: Record<MetaVariant, number> = {
  mercury: 0,
  energy: 1,
  fission: 2,
  spectrum: 3,
  lava: 4,
}

/**
 * Metaballs: blobs that merge/split via a smooth-union SDF, with several variants
 * (set by params.metaVariant):
 *  - mercury  : shaded liquid metal that orbits; bass swells, beat separates.
 *  - energy   : emissive glowing orbs (additive), colour by band, flares on beat.
 *  - fission  : blobs ping-pong in/out (merge then split) in rhythm with the
 *               music; the beat blasts them apart on top.
 *  - spectrum : each blob tied to a frequency band — a liquid equalizer.
 *  - lava     : warm blobs rising/sinking in a tall capsule; energy speeds them.
 */
export class Metaballs extends BasePrimitive {
  object3D: THREE.Mesh
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    const half = new THREE.Vector3(6.5, 7.0, 6.5)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      side: THREE.BackSide,
      blending: THREE.NormalBlending,
      vertexShader: /* glsl */ `
        varying vec3 vWorld;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorld = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uTime, uEnergyTime, uBeatPulse, uBass, uMid, uTreble, uLevel, uBeat, uReactivity, uBassPunch, uHue;
        uniform float uMetaVariant;   // 0 mercury, 1 energy, 2 fission, 3 spectrum, 4 lava
        uniform vec3 uBoxHalf;
        varying vec3 vWorld;
        ${hsv2rgb}
        float smin(float a, float b, float k) {
          float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
          return mix(b, a, h) - k * h * (1.0 - h);
        }
        vec3 orbit(float i, float t) {
          return vec3(
            sin(t * 0.7 + i * 1.3) * 2.1,
            cos(t * 0.6 + i * 2.1) * 1.7,
            sin(t * 0.5 + i * 0.7) * 2.1
          );
        }
        float bandFor(float i) {
          if (i < 0.5) return uBass;
          if (i < 1.5) return uMid;
          if (i < 2.5) return uTreble;
          if (i < 3.5) return uLevel;
          if (i < 4.5) return uBass;
          return uMid;
        }
        // SDF of the blob field; also returns the nearest blob index (for colour).
        float mapV(vec3 p, out float matId) {
          int variant = int(uMetaVariant + 0.5);
          float t = uEnergyTime;
          float d = 1e9, md = 1e9;
          matId = 0.0;
          for (int i = 0; i < 6; i++) {
            float fi = float(i);
            vec3 c; float r;
            if (variant == 4) {                  // lava: smooth rise/sink (sine, no wrap jump)
              float yph = sin(t * 0.45 + fi * 1.9) * 4.0;
              c = vec3(sin(fi * 2.0 + t * 0.2) * 1.7, yph, cos(fi * 1.3) * 1.7);
              r = 1.0 + uBass * uBassPunch * 0.4 * uReactivity;
            } else if (variant == 3) {           // spectrum: blob size per band
              float ang = fi / 6.0 * 6.2831853 + t * 0.15;
              c = vec3(cos(ang) * 2.6, sin(ang) * 2.6, 0.0);
              r = 0.45 + bandFor(fi) * 2.0 * uReactivity;
            } else if (variant == 2) {           // fission: blobs ping-pong in/out; the beat blasts them apart
              vec3 dir = normalize(vec3(sin(fi * 1.7), cos(fi * 2.3), sin(fi * 0.9)) + 0.001);
              // Continuous ping-pong: each blob swings toward the center and back
              // out on its own phase, so they keep merging (fusion) and splitting
              // (fission) in rhythm. The swing speeds up (uEnergyTime is the
              // energy-scaled clock) and widens (uLevel) with the music, so it
              // breathes gently in silence and pumps hard on loud parts.
              float ping = 0.5 - 0.5 * cos(uEnergyTime * 2.2 + fi * 2.39);   // 0..1, per-blob phase
              float swing = ping * (0.9 + uLevel * 2.0 * uReactivity);
              // The beat punches an extra outward blast on top of the swing.
              float sepF = 0.2 + swing + uBeatPulse * 2.2 * uReactivity;
              c = dir * sepF * (1.0 + fi * 0.15) + orbit(fi, t * 0.3) * 0.25;
              // Swell while gathered (ping low) and on the beat so the merge stays gooey.
              r = 0.72 + (1.0 - ping) * 0.3 + uBeatPulse * 0.2 * uReactivity;
            } else {                             // mercury / energy: orbit
              c = orbit(fi, t) * (1.0 + uBeat * 0.9 * uReactivity);
              r = 0.85 + uBass * uBassPunch * 0.7 * uReactivity;
            }
            float raw = length(p - c) - r;
            if (raw < md) { md = raw; matId = fi; }
            d = smin(d, raw, 0.9);
          }
          return d;
        }
        vec3 calcNormal(vec3 p) {
          vec2 e = vec2(0.012, 0.0);
          float m;
          return normalize(vec3(
            mapV(p + e.xyy, m) - mapV(p - e.xyy, m),
            mapV(p + e.yxy, m) - mapV(p - e.yxy, m),
            mapV(p + e.yyx, m) - mapV(p - e.yyx, m)
          ));
        }
        vec3 surfaceColor(vec3 p, vec3 n, vec3 rd, float matId) {
          int variant = int(uMetaVariant + 0.5);
          vec3 v = -rd;
          float fres = pow(1.0 - max(dot(n, v), 0.0), 3.0);
          vec3 refl = reflect(rd, n);
          vec3 L = normalize(vec3(0.5, 1.0, 0.4));
          float spec = pow(max(dot(reflect(-L, n), v), 0.0), 48.0);
          if (variant == 3) {                    // spectrum: per-band hue
            vec3 base = hsv2rgb(vec3(fract(uHue + matId / 6.0), 0.75, 0.6));
            return base + spec + fres * 0.5;
          }
          if (variant == 2 || variant == 4) {    // fission / lava: molten metal
            vec3 hot = mix(vec3(0.55, 0.05, 0.0), vec3(1.0, 0.6, 0.12), fres + spec);
            return hot + spec * 0.5 + vec3(1.0, 0.4, 0.1) * uBeat * 0.6 * uReactivity;
          }
          // mercury: reflective chrome
          vec3 env = mix(vec3(0.05, 0.05, 0.08), hsv2rgb(vec3(uHue, 0.6, 0.95)), refl.y * 0.5 + 0.5);
          vec3 base = hsv2rgb(vec3(uHue, 0.35, 0.5));
          vec3 col = mix(base, env, 0.6) + fres * 0.7 + spec * (1.0 + uBeat * 0.5);
          col += hsv2rgb(vec3(fract(uHue + 0.5), 0.85, 1.0)) * fres * uBeat * 0.8 * uReactivity;
          return col;
        }
        void main() {
          vec3 ro = cameraPosition;
          vec3 rd = normalize(vWorld - ro);
          vec3 t1 = (-uBoxHalf - ro) / rd;
          vec3 t2 = (uBoxHalf - ro) / rd;
          vec3 tmin = min(t1, t2), tmax = max(t1, t2);
          float tn = max(max(tmin.x, tmin.y), tmin.z);
          float tf = min(min(tmax.x, tmax.y), tmax.z);
          tn = max(tn, 0.0);
          if (tn >= tf) discard;
          int variant = int(uMetaVariant + 0.5);

          if (variant == 1) {
            // ENERGY: emissive volumetric accumulation (additive), colour by band.
            const int ESTEPS = 48;
            float dt = (tf - tn) / float(ESTEPS);
            float t = tn + dt * 0.5;
            vec3 acc = vec3(0.0);
            float glow = 1.4 + uLevel * 1.2 + uBeat * 1.6 * uReactivity;
            for (int i = 0; i < ESTEPS; i++) {
              vec3 p = ro + rd * t;
              float m;
              float dm = mapV(p, m);
              float dens = smoothstep(0.35, -0.5, dm);
              if (dens > 0.001) {
                float hue = fract(uHue + m / 6.0 + (uTreble - uBass) * 0.15 * uReactivity);
                acc += hsv2rgb(vec3(hue, 0.8, 1.0)) * dens * dt * glow;
              }
              t += dt;
            }
            if (max(acc.r, max(acc.g, acc.b)) < 0.01) discard;
            gl_FragColor = vec4(acc, 1.0);
            return;
          }

          // SURFACE variants: sphere-trace to the blob surface and shade it.
          float t = tn;
          bool hit = false;
          for (int i = 0; i < 64; i++) {
            vec3 p = ro + rd * t;
            float m;
            float dm = mapV(p, m);
            if (dm < 0.01) { hit = true; break; }
            t += max(dm, 0.02);
            if (t > tf) break;
          }
          if (!hit) discard;
          vec3 p = ro + rd * t;
          float matId;
          mapV(p, matId);
          vec3 n = calcNormal(p);
          gl_FragColor = vec4(surfaceColor(p, n, rd, matId), 1.0);
        }
      `,
    })
    this.material.uniforms.uBoxHalf = { value: half }
    this.material.uniforms.uMetaVariant = { value: 0 }
    this.object3D = new THREE.Mesh(new THREE.BoxGeometry(half.x * 2, half.y * 2, half.z * 2), this.material)
    this.object3D.frustumCulled = false
  }

  update(params: VisualParams): void {
    const id = VARIANT_ID[params.metaVariant ?? 'mercury'] ?? 0
    this.material.uniforms.uMetaVariant.value = id
    // Energy is emissive (additive glow); the rest are shaded surfaces.
    this.material.blending = id === 1 ? THREE.AdditiveBlending : THREE.NormalBlending
  }

  rebuild(): void {}
}
